import React, { useEffect, useState, useCallback } from "react";
import { getApiUrl } from "../config/api";
import { formatDateTime } from "../utils/dateUtils";
import HistoryDetailsModal from "./HistoryDetailsModal";
import { fetchMaterialCatalog, getCatalogForCategory } from "../utils/MaterialCatalog";

function capitalize(str) {
  if (!str) return "";
  // Replace dashes with spaces, then capitalize
  return str.replace(/-/g, ' ').charAt(0).toUpperCase() + str.replace(/-/g, ' ').slice(1);
}

// Convert kebab-case to Title Case for catalog lookup
function toTitleCase(str) {
  if (!str) return "";
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function HistoryItem({ item, onClick, onCancel, onAccept, onDismiss, catalog }) {
  // Helper function to get unit from catalog
  const getUnitForMaterial = (material, subtype) => {
    if (!catalog || !material || !subtype) return 'units';
    
    const materialTitle = toTitleCase(material);
    const subtypeTitle = toTitleCase(subtype);
    
    const categoryItems = getCatalogForCategory(catalog, materialTitle);
    const item = categoryItems.find(item => item.name === subtypeTitle);
    return item?.unit || 'units';
  };

  // State color mapping
  const stateColors = {
    "New Request": "bg-blue-100 text-blue-800",
    "Quoted": "bg-purple-100 text-purple-800",
    "Countered": "bg-yellow-100 text-yellow-800",
    "Last Look": "bg-indigo-100 text-indigo-800",
    "Accepted": "bg-green-100 text-green-800",
    "Awarded": "bg-teal-100 text-teal-800",
    "Rejected": "bg-red-100 text-red-800",
  };
  const stateClass = stateColors[item.state] || "bg-gray-100 text-gray-800";

  const handleCancelClick = async (e) => {
    e.stopPropagation(); // Prevent opening modal
    
    if (!item.rfqNumber) {
      alert("Cannot cancel: No RFQ number");
      return;
    }

    if (!window.confirm(`Cancel RFQ ${item.rfqNumber}?`)) {
      return;
    }

    if (onCancel) {
      onCancel(item);
    }
  };

  const handleAcceptClick = async (e) => {
    e.stopPropagation();
    
    if (!item.rfqNumber) {
      alert("Cannot accept: No RFQ number");
      return;
    }

    if (!window.confirm(`Accept RFQ ${item.rfqNumber}?`)) {
      return;
    }

    if (onAccept) {
      onAccept(item);
    }
  };

  const handleDismissClick = (e) => {
    e.stopPropagation();
    if (onDismiss) {
      onDismiss(item);
    }
  };

  const showCancelButton = item.state !== "Rejected" && item.state !== "Accepted" && item.state !== "Awarded" && item.rfqNumber;
  // Allow accepting when quoted or in a "last look" state (supporting variants like "Last Look", "last-look", "last_look")
  const stateLower = (item.state || "").toLowerCase();
  const showAcceptButton = (stateLower === "quoted" || stateLower.includes("last")) && item.rfqNumber;
  const showDismissButton = item.state === "Awarded" || item.state === "Rejected" || item.state === "Accepted";

  return (
    <div 
      className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 p-2 sm:p-3 shadow-none flex flex-col gap-2 min-w-[420px] max-w-[420px] w-[420px] cursor-pointer hover:shadow-md transition-shadow relative"
      onClick={() => onClick(item)}
    >
      <div className="flex items-center justify-between">
        <span className="font-semibold text-gray-900 dark:text-gray-100 truncate" title={item.event || "Event"}>
          {item.event || "Event"}
        </span>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${stateClass}`}>{item.state}</span>
          
          {/* Dismiss button for completed RFQs */}
          {showDismissButton && (
            <button
              onClick={handleDismissClick}
              className="p-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-full transition-colors flex-shrink-0"
              title="Dismiss"
            >
              <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDateTime(item.time)}</div>

      <div className="space-y-1 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Category:</span>
          <span className="flex-1 min-w-0 text-gray-900 dark:text-gray-100 truncate" title={item.subMaterial}>
            {capitalize(item.subMaterial)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Material:</span>
          <span className="flex-1 min-w-0 text-gray-900 dark:text-gray-100 truncate" title={item.material}>
            {capitalize(item.material)}{item.subMaterial ? ` (${capitalize(item.subMaterial)})` : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Dealer:</span>
          <span className="flex-1 min-w-0 text-gray-900 dark:text-gray-100 truncate" title={item.dealer}>
            {item.dealer}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Qty:</span>
          <span className="flex-1 min-w-0 text-gray-900 dark:text-gray-100 truncate" title={`${item.quantity}`}>
            {item.quantity}
          </span>
        </div>
        {item.rfqNumber && (
          <div className="flex items-center gap-2">
            <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">RFQ#:</span>
            <span className="flex-1 min-w-0 text-gray-900 dark:text-gray-100 truncate" title={item.rfqNumber}>
              {item.rfqNumber}
            </span>
          </div>
        )}
        {item.quote && item.quote.price !== null && item.quote.price !== undefined && (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Quote:</span>
              <span className="flex-1 min-w-0 text-green-700 dark:text-green-400 font-bold text-lg truncate" title={`$${item.quote.price.toLocaleString()}`}>
                ${typeof item.quote.price === 'number' ? item.quote.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : item.quote.price}
              </span>
            </div>
            {item.quantity && (() => {
              const unit = getUnitForMaterial(item.material, item.subMaterial);
              const unitPrice = typeof item.quote.price === 'number' && item.quantity 
                ? (item.quote.price / parseFloat(item.quantity)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : '0.00';
              return (
                <div className="pl-24 text-sm text-gray-600 dark:text-gray-400">
                  ${unitPrice}/{unit}
                </div>
              );
            })()}
          </div>
        )}
        {item.quote && item.quote.deliveryTime && (
          <div className="flex items-center gap-2">
            <span className="w-24 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">Delivery:</span>
            <span className="flex-1 min-w-0 text-gray-900 dark:text-gray-100 truncate" title={item.quote.deliveryTime}>
              {item.quote.deliveryTime}
            </span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {(showAcceptButton || showCancelButton) && (
        <div className="flex gap-2 mt-2">
          {showAcceptButton && (
            <button
              onClick={handleAcceptClick}
              className="flex-1 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors"
            >
              Accept
            </button>
          )}
          {showCancelButton && (
            <button
              onClick={handleCancelClick}
              className="flex-1 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function History({ width, isCollapsed, onToggle, onDragStart, clientId, onRfqSelect, onOpenChat, onHistoryUpdate }) {
  const [items, setItems] = useState([]);
  const [streamStatus, setStreamStatus] = useState("idle");
  const [filterStates, setFilterStates] = useState([]);
  const [filterDealers, setFilterDealers] = useState([]);
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterMaterials, setFilterMaterials] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [catalog, setCatalog] = useState(null);
  const [timeRange, setTimeRange] = useState(60); // minutes
  const [itemLimit, setItemLimit] = useState(100);
  const filterContainerRef = React.useRef(null);

  // Load catalog on mount
  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const catalogData = await fetchMaterialCatalog();
        setCatalog(catalogData);
      } catch (err) {
        console.error('[History] Failed to load catalog:', err);
      }
    };
    loadCatalog();
  }, []);

  // Notify parent when history items change
  useEffect(() => {
    if (onHistoryUpdate && items.length > 0) {
      onHistoryUpdate(items);
    }
  }, [items, onHistoryUpdate]);

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
    // Notify parent about the selected RFQ
    if (onRfqSelect && item.rfqNumber) {
      onRfqSelect({ rfqNumber: item.rfqNumber, dealer: item.dealer, material: item.material });
    }
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedItem(null);
  };

  const handleItemUpdate = (updatedItem) => {
    // Update the item in the list, preserving quote data if not in updatedItem
    // Also move the updated item to the top of the list so notifications surface
    setItems((prev) => {
      if (!updatedItem || !updatedItem.rfqNumber) {
        // If there's no rfqNumber, fallback to updating by id
        const byId = updatedItem.id;
        return prev.map(item => item.id === byId ? { ...item, ...updatedItem } : item);
      }

      // Preserve existing quote if new update lacks it
      const prevMatch = prev.find(item => item.rfqNumber === updatedItem.rfqNumber);
      const preservedQuote = (updatedItem.quote === undefined || updatedItem.quote === null) && prevMatch ? prevMatch.quote : updatedItem.quote;

        const merged = { ...(prevMatch || {}), ...updatedItem, quote: preservedQuote };

        // Remove any previous instance of this rfqNumber
        const others = prev.filter(item => item.rfqNumber !== updatedItem.rfqNumber);

        // Place the merged item at the top and sort by time descending
        return [merged, ...others]
          .sort((a, b) => new Date(b.time) - new Date(a.time))
          .slice(0, 100);
      });
  };

  const handleAcceptItem = async (item) => {
    try {
      const response = await fetch(getApiUrl(`/api/rfq/${item.rfqNumber}/accept?clientId=${encodeURIComponent(clientId)}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rfqId: item.rfqNumber,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to accept RFQ: ${response.status}`);
      }

      let data = null;
      try {
        const txt = await response.text();
        data = txt ? JSON.parse(txt) : null;
      } catch (err) {
        data = null;
      }
      console.log("RFQ accepted:", data);

      handleItemUpdate({ ...item, state: "Accepted", status: "accepted" });
    } catch (err) {
      console.error("Accept error:", err);
      alert(err.message || "Failed to accept RFQ");
    }
  };

  const handleDismissItem = (item) => {
    // Remove the item from the local list
    setItems((prev) => prev.filter((it) => it.id !== item.id));
  };

  const handleCancelItem = async (item) => {
    try {
      const response = await fetch(getApiUrl(`/api/rfq/${item.rfqNumber}/reject?clientId=${encodeURIComponent(clientId)}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rfqId: item.rfqNumber,
          reason: "Cancelled by client",
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to cancel RFQ: ${response.status}`);
      }

      // Guard against empty/non-JSON responses
      let data = null;
      try {
        const txt = await response.text();
        data = txt ? JSON.parse(txt) : null;
      } catch (err) {
        data = null;
      }
      console.log("RFQ cancelled:", data);

      // Update the item in the list
      handleItemUpdate({ ...item, state: "Rejected", status: "rejected" });
    } catch (err) {
      console.error("Cancel error:", err);
      alert(err.message || "Failed to cancel RFQ");
    }
  };

  // Move normalizeItem outside useEffect so it can be reused
  const normalizeItem = useCallback((historyItem, fallbackTs) => {
    const rfq = historyItem.rfq || {};
    const quote = historyItem.quote || null;
    
    console.log('[History] Normalizing item:', historyItem, " Normalized:", {
      id: historyItem.id,
      state: rfq.state || historyItem.state,
      hasQuote: !!quote,
      quote: quote,
      fullItem: historyItem
    });
    
    // Use lastInteractionTime if available, otherwise fall back to createdAt or quote times
    const interactionTime = rfq.lastInteractionTime 
      || historyItem.lastInteractionTime 
      || quote?.updatedAt 
      || quote?.createdAt 
      || historyItem.createdAt 
      || fallbackTs 
      || new Date().toISOString();
    
    return {
      id: historyItem.id || `history-${Date.now()}-${Math.random()}`,
      event: historyItem.event || "",
      dealer: historyItem.dealerCompany || historyItem.dealerName || historyItem.dealer?.name || historyItem.dealer || "Company",
      dealerId: historyItem.dealerId || historyItem.dealer?.id || null,
      state: rfq.location?.state || rfq.state || historyItem.location?.state || historyItem.state || "N/A",
      quantity: rfq.quantity ?? historyItem.quantity ?? -1,
      material: rfq.material || historyItem.material || "N/A",
      subMaterial: rfq.subtype || historyItem.subtype || "",
      materialCategory: rfq.materialCategory || historyItem.materialCategory || "",
      rfqNumber: rfq.rfqNumber || historyItem.rfqNumber,
      rfqId: rfq.id || historyItem.rfqId || null,
      status: rfq.status || historyItem.status,
      time: interactionTime,
      dealerName: historyItem.dealerName,
      notes: rfq.notes || historyItem.notes || null,
      quote: quote ? {
        id: quote.id,
        price: quote.price,
        deliveryTime: quote.deliveryTime,
        terms: quote.terms,
        notes: quote.notes,
        validUntil: quote.validUntil,
        createdAt: quote.createdAt,
        updatedAt: quote.updatedAt,
      } : null,
    };
  }, []);

  useEffect(() => {
    let es;
    let retryTimeout;
    let isCancelled = false;
    const abortController = new AbortController();

    const handleMessage = (event) => {
      console.log('[History] Received SSE message:', event);
      console.log('[History] Message data:', event.data);
      try {
        const raw = JSON.parse(event.data);
        console.log('[History] Parsed message:', raw);
        if (!raw.newHistory || !Array.isArray(raw.newHistory) || raw.newHistory.length === 0) {
          console.log('[History] Message has no valid newHistory array');
          return;
        }
        
        // Filter items by time range BEFORE normalizing
        const now = new Date();
        const timeRangeMs = timeRange * 60 * 1000;
        const filteredRaw = raw.newHistory.filter(historyItem => {
          // Check various timestamp fields
          const itemTime = historyItem.createdAt 
            || historyItem.rfq?.createdAt 
            || historyItem.rfq?.lastInteractionTime
            || historyItem.lastInteractionTime;
          
          if (!itemTime) {
            console.log('[History] Item has no timestamp, including it:', historyItem.id);
            return true; // Include items without timestamp
          }
          
          const itemDate = new Date(itemTime + (itemTime.endsWith('Z') ? '' : 'Z'));
          const age = now - itemDate;
          const withinRange = age <= timeRangeMs;
          
          if (!withinRange) {
            console.log('[History] Filtering out old item:', historyItem.id, 'age:', Math.round(age / 60000), 'minutes');
          }
          
          return withinRange;
        });
        
        if (filteredRaw.length === 0) {
          console.log('[History] All items filtered out by time range');
          return;
        }
        
        console.log('[History] Filtered by time range:', filteredRaw.length, 'of', raw.newHistory.length, 'items');
        
        const newItems = filteredRaw.map((historyItem) =>
          normalizeItem(historyItem, raw.timestamp)
        );
        console.log('[History] Normalized items:', newItems);
        
        setItems((prev) => {
          // Remove any existing items with the same rfqNumber as incoming items
          const newRfqNumbers = new Set(
            newItems.filter(item => item.rfqNumber).map(item => item.rfqNumber)
          );
          
          // Create a map of previous items by rfqNumber to preserve quote data
          const prevItemsMap = new Map();
          prev.forEach(item => {
            if (item.rfqNumber) {
              prevItemsMap.set(item.rfqNumber, item);
            }
          });
          
          // Merge new items with previous quote data if quote is missing in new item
          const mergedNewItems = newItems.map(newItem => {
            if (newItem.rfqNumber && prevItemsMap.has(newItem.rfqNumber)) {
              const prevItem = prevItemsMap.get(newItem.rfqNumber);
              // If new item doesn't have quote but previous item did, preserve it
              if (!newItem.quote && prevItem.quote) {
                console.log('[History] Preserving quote data for RFQ:', newItem.rfqNumber);
                return { ...newItem, quote: prevItem.quote };
              }
            }
            return newItem;
          });
          
          const filteredPrev = prev.filter(item => 
            !item.rfqNumber || !newRfqNumbers.has(item.rfqNumber)
          );
          
          // Add new items to the top and limit to 100 items, then sort by time descending
          const updated = [...mergedNewItems, ...filteredPrev]
            .sort((a, b) => new Date(b.time) - new Date(a.time))
            .slice(0, 100);
          console.log('[History] Updated items list, total:', updated.length);
          return updated;
        });
      } catch (err) {
        console.warn("SSE message parse error:", err, event?.data);
      }
    };

    const connect = () => {
      // Build stream URL and include clientId if provided
      const streamUrl = clientId
        ? getApiUrl(`/api/history/stream?clientId=${encodeURIComponent(clientId)}`)
        : getApiUrl('/api/history/stream');

      console.log('[History] Attempting to connect to SSE stream at:', streamUrl);
      setStreamStatus("connecting");
      es = new EventSource(streamUrl);
      
      es.onopen = () => {
        console.log('[History] SSE connection opened successfully');
        setStreamStatus("open");
      };
      
      // Listen to default message event
      es.onmessage = (event) => {
        console.log('[History] Received default message event');
        handleMessage(event);
      };
      
      // Also listen to "history" named event
      es.addEventListener("history", (event) => {
        console.log('[History] Received history named event');
        handleMessage(event);
      });
      
      // Listen to any other message type
      es.addEventListener("message", (event) => {
        console.log('[History] Received message event via addEventListener');
        handleMessage(event);
      });
      
      es.onerror = (error) => {
        console.error('[History] SSE connection error:', error);
        console.log('[History] EventSource readyState:', es.readyState);
        setStreamStatus("error");
        try { es.close(); } catch {}
        retryTimeout = setTimeout(() => {
          console.log('[History] Retrying connection...');
          connect();
        }, 3000);
      };
    };

    const loadInitial = async () => {
      // If no clientId provided, skip initial fetch and connect immediately
      if (!clientId) {
        console.log('[History] No clientId provided, connecting directly to stream');
        connect();
        return;
      }

      console.log('[History] Loading initial history for clientId:', clientId);
      setStreamStatus("loading");
      try {
        const url = getApiUrl(`/api/history/client/${encodeURIComponent(clientId)}?minutes=${timeRange}&limit=${itemLimit}`);
        console.log('[History] Fetching initial history from:', url);
        const res = await fetch(url, {
          signal: abortController.signal,
        });
        if (!res.ok) throw new Error(`Initial history fetch failed: ${res.status}`);
        const data = await res.json();
        console.log('[History] Fetched initial history data:', data);
        const rawArr =
          Array.isArray(data) ? data :
          data?.history ?? data?.items ?? data?.newHistory ?? [];
        const tsNow = new Date().toISOString();
        const mapped = (rawArr || []).map((it) => normalizeItem(it, tsNow));
        
        // Deduplicate by rfqNumber, keeping only the most recent (first occurrence after sorting by time)
        const deduped = [];
        const seenRfqNumbers = new Set();
        
        // Sort by time descending (newest first)
        const sorted = mapped.sort((a, b) => new Date(b.time) - new Date(a.time));
        
        for (const item of sorted) {
          if (item.rfqNumber) {
            if (!seenRfqNumbers.has(item.rfqNumber)) {
              seenRfqNumbers.add(item.rfqNumber);
              deduped.push(item);
            }
          } else {
            // Items without rfqNumber are always included
            deduped.push(item);
          }
        }
        
        if (!isCancelled) {
          console.log('[History] Initial history loaded:', mapped.length, 'items, deduplicated to:', deduped.length);
          setItems(deduped.slice(0, 100));
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.warn("[History] Initial history load failed:", e);
        }
      } finally {
        if (!isCancelled) {
          console.log('[History] Initial load complete, now connecting to stream...');
          connect();
        }
      }
    };
    loadInitial();

    return () => {
      isCancelled = true;
      abortController.abort();
      if (es) {
        try { es.close(); } catch {}
      }
      if (retryTimeout) clearTimeout(retryTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, normalizeItem]); // Only reconnect on clientId change (timeRange/itemLimit excluded intentionally)
  
  // Separate effect to reload initial data when timeRange or itemLimit changes
  useEffect(() => {
    if (!clientId) return;
    
    const abortController = new AbortController();
    
    const reloadData = async () => {
      console.log('[History] Reloading data for new time range/limit:', timeRange, itemLimit);
      try {
        const url = getApiUrl(`/api/history/client/${encodeURIComponent(clientId)}?minutes=${timeRange}&limit=${itemLimit}`);
        const res = await fetch(url, {
          signal: abortController.signal,
        });
        if (!res.ok) throw new Error(`History reload failed: ${res.status}`);
        const data = await res.json();
        const rawArr =
          Array.isArray(data) ? data :
          data?.history ?? data?.items ?? data?.newHistory ?? [];
        const tsNow = new Date().toISOString();
        const mapped = (rawArr || []).map((it) => normalizeItem(it, tsNow));
        
        // Deduplicate by rfqNumber
        const deduped = [];
        const seenRfqNumbers = new Set();
        const sorted = mapped.sort((a, b) => new Date(b.time) - new Date(a.time));
        
        for (const item of sorted) {
          if (item.rfqNumber) {
            if (!seenRfqNumbers.has(item.rfqNumber)) {
              seenRfqNumbers.add(item.rfqNumber);
              deduped.push(item);
            }
          } else {
            deduped.push(item);
          }
        }
        
        console.log('[History] Data reloaded:', deduped.length, 'items');
        setItems(deduped.slice(0, 100));
      } catch (e) {
        if (e.name !== "AbortError") {
          console.warn("[History] Data reload failed:", e);
        }
      }
    };
    
    reloadData();
    
    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, itemLimit]); // Reload data when filters change (clientId and normalizeItem are stable)

  // Periodic cleanup of items outside the time range
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setItems((prev) => {
        const now = new Date();
        const timeRangeMs = timeRange * 60 * 1000;
        
        const filtered = prev.filter(item => {
          if (!item.time) return true; // Keep items without timestamp
          const itemTime = new Date(item.time);
          const age = now - itemTime;
          return age <= timeRangeMs;
        });
        
        if (filtered.length !== prev.length) {
          console.log('[History] Cleaned up old items:', prev.length - filtered.length, 'removed');
        }
        
        return filtered;
      });
    }, 60000); // Run every minute

    return () => clearInterval(cleanupInterval);
  }, [timeRange]);

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterContainerRef.current && !filterContainerRef.current.contains(event.target)) {
        // Close all open details elements
        const openDetails = filterContainerRef.current.querySelectorAll('details[open]');
        openDetails.forEach(details => {
          details.removeAttribute('open');
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Build options
  const allStates = Array.from(new Set(items.map(it => it.state).filter(Boolean)));
  const allDealers = Array.from(new Set(items.map(it => it.dealer).filter(Boolean)));
  const allCategories = Array.from(new Set(items.map(it => it.subMaterial).filter(Boolean)));
  const allMaterials = Array.from(new Set(items.map(it => it.material).filter(Boolean)));

  // Toggle filter selection
  const toggleFilter = (setFilter, value) => {
    setFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  // Apply all filters (multi-select OR logic within each category, AND logic across categories)
  const filteredItems = items.filter(it => {
    const stateMatch = filterStates.length === 0 || filterStates.includes(it.state);
    const dealerMatch = filterDealers.length === 0 || filterDealers.includes(it.dealer);
    const categoryMatch = filterCategories.length === 0 || filterCategories.includes(it.subMaterial);
    const materialMatch = filterMaterials.length === 0 || filterMaterials.includes(it.material);
    
    // Search filter - search by RFQ number, dealer, material
    const searchMatch = !searchQuery.trim() || 
      (it.rfqNumber && it.rfqNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (it.dealer && it.dealer.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (it.material && it.material.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return stateMatch && dealerMatch && categoryMatch && materialMatch && searchMatch;
  });

  // Active filters to display
  const activeFilters = [
    ...filterStates.map(v => ({ label: "State", value: v })),
    ...filterDealers.map(v => ({ label: "Dealer", value: v })),
    ...filterCategories.map(v => ({ label: "Category", value: v, cap: true })),
    ...filterMaterials.map(v => ({ label: "Material", value: v, cap: true })),
  ];

  // Clear all filters
  const clearFilters = () => {
    setFilterStates([]);
    setFilterDealers([]);
    setFilterCategories([]);
    setFilterMaterials([]);
  };

  // Hide content if width is very small (e.g., < 120px)
  const hideContent = isCollapsed || width < 120;

  return (
    <>
      {!isCollapsed && (
        <div
          className="w-1 box-border bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 border-l border-gray-400 dark:border-gray-600 cursor-ew-resize transition-colors select-none"
          onMouseDown={onDragStart}
        />
      )}

      <aside
        className={`bg-gray-300 dark:bg-gray-800 py-6 px-0 relative overflow-hidden ${isCollapsed ? 'border-l border-gray-400 dark:border-gray-600' : ''}`}
        style={{ width: isCollapsed ? '40px' : `${width}px`, height: "100%" }}
      >
        <button
          onClick={onToggle}
          className="absolute top-4 left-2 p-1 bg-gray-500 dark:bg-gray-700 hover:bg-gray-600 dark:hover:bg-gray-600 text-white rounded transition-colors"
          title={isCollapsed ? "Expand History" : "Collapse History"}
        >
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {!hideContent && (
          <div className="mt-8 h-full flex flex-col min-w-0">
            <div className="flex items-center justify-between whitespace-nowrap px-2 flex-shrink-0">
              <h2 className="font-semibold text-lg text-gray-900 dark:text-gray-100">History</h2>
              <span
                className={`text-xs px-2 py-0.5 rounded ${
                  streamStatus === "open"
                    ? "bg-green-200 dark:bg-green-900 text-green-800 dark:text-green-200"
                    : (streamStatus === "connecting" || streamStatus === "loading")
                    ? "bg-yellow-200 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200"
                    : streamStatus === "error"
                    ? "bg-red-200 dark:bg-red-900 text-red-800 dark:text-red-200"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                }`}
                title="Stream status"
              >
                {streamStatus}
              </span>
            </div>

            {/* Search bar */}
            <div className="mt-4 px-2 flex-shrink-0">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by RFQ #, dealer, material..."
                className="w-full px-3 py-1.5 text-sm border border-gray-400 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Time Range and Limit Controls */}
            <div className="mt-2 px-2 flex gap-2 flex-shrink-0">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Time Range
                </label>
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(Number(e.target.value))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-400 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={30}>Last 30 min</option>
                  <option value={60}>Last hour</option>
                  <option value={180}>Last 3 hours</option>
                  <option value={360}>Last 6 hours</option>
                  <option value={720}>Last 12 hours</option>
                  <option value={1440}>Last 24 hours</option>
                  <option value={4320}>Last 3 days</option>
                  <option value={10080}>Last week</option>
                  <option value={43200}>Last month</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Items
                </label>
                <select
                  value={itemLimit}
                  onChange={(e) => setItemLimit(Number(e.target.value))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-400 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                </select>
              </div>
            </div>

            {/* Multi-select filter row */}
            <div className="mt-2 mb-2 px-2 flex-shrink-0" style={{ background: "transparent" }} ref={filterContainerRef}>
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-gray-700">Filter:</span>
                
                <div className="flex flex-wrap gap-2 items-start">
                  {/* State - Multi-select with checkboxes */}
                  <details className="group">
                    <summary className={`cursor-pointer list-none px-3 py-1.5 text-sm rounded-sm border ${
                      filterStates.length > 0
                        ? "bg-blue-100 text-blue-800 border-blue-300"
                        : "bg-white text-gray-700 border-gray-300"
                    } hover:bg-gray-50`}>
                      State {filterStates.length > 0 && `(${filterStates.length})`}
                    </summary>
                    <div className="mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg min-w-[200px] max-h-[300px] overflow-y-auto">
                      {allStates.map((state) => (
                        <label key={state} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filterStates.includes(state)}
                            onChange={() => toggleFilter(setFilterStates, state)}
                            className="rounded"
                          />
                          <span className="text-sm">{state}</span>
                        </label>
                      ))}
                    </div>
                  </details>

                  {/* Dealer - Multi-select with checkboxes */}
                  <details className="group">
                    <summary className={`cursor-pointer list-none px-3 py-1.5 text-sm rounded-sm border ${
                      filterDealers.length > 0
                        ? "bg-blue-100 text-blue-800 border-blue-300"
                        : "bg-white text-gray-700 border-gray-300"
                    } hover:bg-gray-50`}>
                      Dealer {filterDealers.length > 0 && `(${filterDealers.length})`}
                    </summary>
                    <div className="mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg min-w-[200px] max-h-[300px] overflow-y-auto">
                      {allDealers.map((dealer) => (
                        <label key={dealer} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filterDealers.includes(dealer)}
                            onChange={() => toggleFilter(setFilterDealers, dealer)}
                            className="rounded"
                          />
                          <span className="text-sm">{dealer}</span>
                        </label>
                      ))}
                    </div>
                  </details>

                  {/* Category - Multi-select with checkboxes */}
                  <details className="group">
                    <summary className={`cursor-pointer list-none px-3 py-1.5 text-sm rounded-sm border ${
                      filterCategories.length > 0
                        ? "bg-blue-100 text-blue-800 border-blue-300"
                        : "bg-white text-gray-700 border-gray-300"
                    } hover:bg-gray-50`}>
                      Category {filterCategories.length > 0 && `(${filterCategories.length})`}
                    </summary>
                    <div className="mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg min-w-[200px] max-h-[300px] overflow-y-auto">
                      {allCategories.map((cat) => (
                        <label key={cat} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filterCategories.includes(cat)}
                            onChange={() => toggleFilter(setFilterCategories, cat)}
                            className="rounded"
                          />
                          <span className="text-sm">{capitalize(cat)}</span>
                        </label>
                      ))}
                    </div>
                  </details>

                  {/* Material - Multi-select with checkboxes */}
                  <details className="group">
                    <summary className={`cursor-pointer list-none px-3 py-1.5 text-sm rounded-sm border ${
                      filterMaterials.length > 0
                        ? "bg-blue-100 text-blue-800 border-blue-300"
                        : "bg-white text-gray-700 border-gray-300"
                    } hover:bg-gray-50`}>
                      Material {filterMaterials.length > 0 && `(${filterMaterials.length})`}
                    </summary>
                    <div className="mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg min-w-[200px] max-h-[300px] overflow-y-auto">
                      {allMaterials.map((mat) => (
                        <label key={mat} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filterMaterials.includes(mat)}
                            onChange={() => toggleFilter(setFilterMaterials, mat)}
                            className="rounded"
                          />
                          <span className="text-sm">{capitalize(mat)}</span>
                        </label>
                      ))}
                    </div>
                  </details>

                  {/* Clear filters button - stays at fixed height with summary elements */}
                  <button
                    type="button"
                    onClick={clearFilters}
                    disabled={activeFilters.length === 0}
                    className="text-xs border rounded-sm px-3 py-1.5 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 shadow-none hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed h-[34px] text-gray-900 dark:text-gray-100"
                    aria-label="Clear all filters"
                    title="Clear all filters"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>

            {/* Active filters indicator */}
            <div className="mb-2 px-2 flex-shrink-0">
              {activeFilters.length > 0 ? (
                <div className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
                  <span className="text-xs text-gray-600 shrink-0">Active:</span>
                  <div className="flex items-center gap-1 overflow-hidden">
                    {activeFilters.map((f) => (
                      <span
                        key={`${f.label}-${f.value}`}
                        className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-sm px-2 py-0.5 truncate max-w-[8rem]"
                        title={`${f.label}: ${f.cap ? capitalize(f.value) : f.value}`}
                      >
                        {f.label}: {f.cap ? capitalize(f.value) : f.value}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-gray-500 whitespace-nowrap">No filters applied</div>
              )}
            </div>

            <div className="flex-1 space-y-2 overflow-y-auto overflow-x-hidden px-0 min-h-0">
              {filteredItems.length === 0 && (
                <div className="text-sm text-gray-700 px-2">No history yet.</div>
              )}
              {filteredItems.map((it) => (
                <div className="px-2" key={it.id}>
                  <HistoryItem 
                    item={it} 
                    onClick={handleItemClick}
                    onCancel={handleCancelItem}
                    onAccept={handleAcceptItem}
                    onDismiss={handleDismissItem}
                    catalog={catalog}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {/* When collapsed or shrunk, hide the history list and filter, only show aside and collapse button */}
      </aside>

      {/* Details Modal */}
      {showDetailsModal && selectedItem && (
        <HistoryDetailsModal
          item={selectedItem}
          onClose={handleCloseModal}
          onUpdate={handleItemUpdate}
          clientId={clientId}
          onOpenChat={(rfq) => {
            if (onOpenChat) onOpenChat(rfq);
            handleCloseModal();
          }}
        />
      )}
    </>
  );
}
